import { CMS_BASE_URL } from './constants'
import {
  extractStrapiMediaUrl,
  readString,
  toStrapiEntity,
  toStrapiEntityCollection,
} from './utils/strapiPayload'

import type {
  SoundcoreCapabilityCardCmsContent,
  SoundcoreFeatureBlockCmsContent,
  SoundcorePageCmsContent,
  SoundcorePartnerProductCmsContent,
  SoundcorePerformanceSpecCmsContent,
  SoundcoreTestimonialsItemCmsContent,
} from './interfaces'

interface FetchSoundcoreContentOptions {
  signal?: AbortSignal
}

const SOUNDCORE_PAGE_ENDPOINT = `/api/soundcore-page?${
  [
    'populate[ctaBackgroundImage]=true',
    'populate[featureBlocks][populate][imageDesktop][fields][0]=url',
    'populate[featureBlocks][populate][imageMobile][fields][0]=url',
    'populate[featureBlocks][populate][contentItems]=true',
    'populate[flagshipButton]=true',
    'populate[partnerProducts][populate][imageDesktop][fields][0]=url',
    'populate[partnerProducts][populate][imageMobile][fields][0]=url',
    'populate[testimonials][populate][avatar][fields][0]=url',
    'populate[capabilitiesCards][populate][desktopImage][fields][0]=url',
    'populate[capabilitiesCards][populate][mobileImage][fields][0]=url',
    'populate[performanceSpecsCards]=true',
  ].join('&')
}`

const readMediaUrl = (value: unknown): string | null => extractStrapiMediaUrl(value, CMS_BASE_URL)

const readOptionalString = (value: unknown): string | undefined => {
  const text = readString(value)

  return text ?? undefined
}

const mapFeatureBlocks = (value: unknown): SoundcoreFeatureBlockCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((block): SoundcoreFeatureBlockCmsContent[] => {
      const imageDesktopUrl = readMediaUrl(block.imageDesktop)
      const imageMobileUrl = readMediaUrl(block.imageMobile)
      const title = readString(block.title)
      const pillText = readString(block.pillText)
      const contentHeading = readString(block.contentHeading)
      const contentItems = toStrapiEntityCollection(block.contentItems)
        .map((item) => readString(item.text))
        .filter((item): item is string => item !== null)

      if (
        imageDesktopUrl === null ||
        imageMobileUrl === null ||
        title === null ||
        pillText === null ||
        contentHeading === null ||
        contentItems.length === 0
      ) {
        return []
      }

      return [{
        imageDesktopUrl,
        imageMobileUrl,
        title,
        pillText,
        contentHeading,
        contentItems,
      }]
    })
)

const mapPartnerProducts = (value: unknown): SoundcorePartnerProductCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((product): SoundcorePartnerProductCmsContent[] => {
      const title = readString(product.title)
      const description = readString(product.description)
      const imageDesktopUrl = readMediaUrl(product.imageDesktop)
      const imageMobileUrl = readMediaUrl(product.imageMobile)
      const buttonText = readString(product.ctaLabel)
      const buttonUrl = readString(product.ctaUrl)

      if (
        title === null ||
        description === null ||
        imageDesktopUrl === null ||
        imageMobileUrl === null ||
        buttonText === null ||
        buttonUrl === null
      ) {
        return []
      }

      return [{ title, description, imageDesktopUrl, imageMobileUrl, buttonText, buttonUrl }]
    })
)

const mapTestimonials = (value: unknown): SoundcoreTestimonialsItemCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((testimonial): SoundcoreTestimonialsItemCmsContent[] => {
      const text = readString(testimonial.quote)
      const name = readString(testimonial.name)
      const title = readString(testimonial.title) ?? ''
      const avatarSrc = readMediaUrl(testimonial.avatar)

      if (text === null || name === null || avatarSrc === null) {
        return []
      }

      return [{
        text,
        name,
        title,
        avatarSrc,
        avatarLink: readOptionalString(testimonial.avatarLink),
      }]
    })
)

const mapCapabilities = (value: unknown): SoundcoreCapabilityCardCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((card): SoundcoreCapabilityCardCmsContent[] => {
      const title = readString(card.title)
      const desktopImageUrl = readMediaUrl(card.desktopImage)
      const mobileImageUrl = readMediaUrl(card.mobileImage)

      if (title === null || desktopImageUrl === null || mobileImageUrl === null) {
        return []
      }

      return [{ title, desktopImageUrl, mobileImageUrl }]
    })
)

const mapPerformanceSpecs = (value: unknown): SoundcorePerformanceSpecCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((spec): SoundcorePerformanceSpecCmsContent[] => {
      const title = readString(spec.title)
      const body = readString(spec.body)

      if (title === null || body === null) {
        return []
      }

      return [{ title, body }]
    })
)

const mapSoundcorePageContent = (payload: unknown): SoundcorePageCmsContent | null => {
  const payloadRoot = toStrapiEntity(payload)
  const contentRoot = toStrapiEntity(payloadRoot?.data || payloadRoot)

  if (!contentRoot) {
    return null
  }

  const heroPillText = readString(contentRoot.heroPillText)
  const heroTitleText = readString(contentRoot.heroTitleText)
  const heroDescription = readString(contentRoot.heroDescription)

  const ctaText = readString(contentRoot.ctaText)
  const ctaBackgroundImageUrl = readMediaUrl(contentRoot.ctaBackgroundImage)

  const featuresTitle = readString(contentRoot.featuresTitle)
  const featureBlocks = mapFeatureBlocks(contentRoot.featureBlocks)

  const flagshipSubtitle = readString(contentRoot.flagshipSubtitle)
  const flagshipTitleText = readString(contentRoot.flagshipTitleText)
  const flagshipDescription = readString(contentRoot.flagshipDescription)
  const flagshipButton = toStrapiEntity(contentRoot.flagshipButton)
  const flagshipButtonText = readString(flagshipButton?.label)
  const flagshipButtonUrl = readString(flagshipButton?.url)

  const partnerProducts = mapPartnerProducts(contentRoot.partnerProducts)
  const testimonialsTitle = readString(contentRoot.testimonialsTitle)
  const testimonials = mapTestimonials(contentRoot.testimonials)
  const capabilitiesTitle = readString(contentRoot.capabilitiesTitle)
  const capabilitiesCards = mapCapabilities(contentRoot.capabilitiesCards)

  const performanceSpecsTitle = readString(contentRoot.performanceSpecsTitle)
  const performanceSpecsCards = mapPerformanceSpecs(contentRoot.performanceSpecsCards)
  const integrationSupportTitle = readString(contentRoot.integrationSupportTitle)
  const integrationSupportBody = readString(contentRoot.integrationSupportBody)

  const bottomHeroTitleLine1 = readString(contentRoot.bottomHeroTitleLine1)
  const bottomHeroTitleLine2 = readOptionalString(contentRoot.bottomHeroTitleLine2)
  const bottomHeroButtonText = readString(contentRoot.bottomHeroButtonText)
  const bottomHeroButtonUrl = readString(contentRoot.bottomHeroButtonUrl)

  if (
    heroPillText === null ||
    heroTitleText === null ||
    heroDescription === null ||
    ctaText === null ||
    ctaBackgroundImageUrl === null ||
    featuresTitle === null ||
    featureBlocks.length !== 3 ||
    flagshipSubtitle === null ||
    flagshipTitleText === null ||
    flagshipDescription === null ||
    flagshipButtonText === null ||
    flagshipButtonUrl === null ||
    partnerProducts.length !== 2 ||
    testimonialsTitle === null ||
    testimonials.length !== 6 ||
    capabilitiesTitle === null ||
    capabilitiesCards.length !== 4 ||
    performanceSpecsTitle === null ||
    performanceSpecsCards.length !== 6 ||
    integrationSupportTitle === null ||
    integrationSupportBody === null ||
    bottomHeroTitleLine1 === null ||
    bottomHeroButtonText === null ||
    bottomHeroButtonUrl === null
  ) {
    return null
  }

  return {
    hero: {
      pillText: heroPillText,
      titleText: heroTitleText,
      description: heroDescription,
    },
    cta: {
      text: ctaText,
      backgroundImageUrl: ctaBackgroundImageUrl,
    },
    features: {
      title: featuresTitle,
      blocks: featureBlocks,
    },
    flagshipProduct: {
      subtitle: flagshipSubtitle,
      titleText: flagshipTitleText,
      description: flagshipDescription,
      buttonText: flagshipButtonText,
      buttonUrl: flagshipButtonUrl,
    },
    partnerProducts,
    testimonials: {
      title: testimonialsTitle,
      items: testimonials,
    },
    capabilities: {
      title: capabilitiesTitle,
      cards: capabilitiesCards,
    },
    performanceSpecs: {
      title: performanceSpecsTitle,
      cards: performanceSpecsCards,
      integrationSupportTitle,
      integrationSupportBody,
    },
    bottomHero: {
      titleLine1: bottomHeroTitleLine1,
      titleLine2: bottomHeroTitleLine2,
      buttonText: bottomHeroButtonText,
      buttonUrl: bottomHeroButtonUrl,
    },
  }
}

export const fetchSoundcoreContentFromStrapi = async (
  options: FetchSoundcoreContentOptions = {},
): Promise<SoundcorePageCmsContent | null> => {
  const response = await fetch(`${CMS_BASE_URL}${SOUNDCORE_PAGE_ENDPOINT}`, {
    signal: options.signal,
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch Soundcore CMS content: ${response.status}`)
  }

  const content = mapSoundcorePageContent(await response.json())

  if (!content) {
    throw new Error('Soundcore CMS content missing required fields')
  }

  return content
}
